/**
 * Слой контракта: типы и разбор конвертов ответа.
 *
 * Типы берутся из сгенерированной схемы, а не описываются здесь заново.
 * Ровно поэтому расхождение с contract/openapi.yaml обязано ломать typecheck:
 * рукописная копия формы данных расходится с контрактом молча и всплывает
 * только на живом backend.
 *
 * Бизнес-правил в этом файле нет и быть не может: клиент отображает, сервер
 * решает. Здесь только форма — распаковка конверта и опознание ошибки.
 */

import type { components } from './generated/schema';

export type User = components['schemas']['User'];
export type Company = components['schemas']['Company'];
export type Membership = components['schemas']['Membership'];
export type Work = components['schemas']['Work'];
export type PageMeta = components['schemas']['PageMeta'];
export type ApiError = components['schemas']['Error'];
export type ErrorEnvelope = components['schemas']['ErrorEnvelope'];

// Формы блока `WEB-1.1`. Берутся из той же сгенерированной схемы: рукописная
// копия формы данных расходится с контрактом молча.
export type CompanyCreateRequest = components['schemas']['CompanyCreateRequest'];
export type CompanyMember = components['schemas']['CompanyMember'];
export type ErrorDetail = components['schemas']['ErrorDetail'];

// Формы блока `WEB-1.2` — кабинет директора и профиль пользователя. Берутся
// из той же сгенерированной схемы по той же причине: рукописная копия формы
// расходится с контрактом молча.
export type Session = components['schemas']['Session'];
export type Invitation = components['schemas']['Invitation'];
export type MemberPermissions = components['schemas']['MemberPermissions'];
export type Permission = components['schemas']['Permission'];
export type Document = components['schemas']['Document'];
export type DocumentLink = components['schemas']['DocumentLink'];

// Формы блока `WEB-1.3` — транспорт фирмы, цикл ремонта, сервисная история и
// заявки на освобождение VIN. Берутся из той же сгенерированной схемы: та же
// причина дословно — рукописная копия формы расходится с контрактом молча.
export type Vehicle = components['schemas']['Vehicle'];
export type VehicleCreateRequest = components['schemas']['VehicleCreateRequest'];
export type RepairStateChangeRequest = components['schemas']['RepairStateChangeRequest'];
export type ServiceRecord = components['schemas']['ServiceRecord'];
export type ServiceRecordCreateRequest = components['schemas']['ServiceRecordCreateRequest'];
export type VinClaim = components['schemas']['VinClaim'];
export type VinClaimCreateRequest = components['schemas']['VinClaimCreateRequest'];

// Форма блока `WEB-3.2` — что видно по публичной ссылке с PIN. Берётся из той
// же сгенерированной схемы: закрытый перечень полей страницы — это перечень
// полей контракта, и рукописная копия разошлась бы с ним молча.
export type PublicTracking = components['schemas']['PublicTracking'];

// Формы блока `WEB-2.1` — кабинет логиста: объявление о грузе, его форма,
// offer'ы, слоты доски и два справочника. Берутся из той же сгенерированной
// схемы: километраж, страны транзита, `manageable` и цену считает сервер, и
// рукописная копия формы разошлась бы с ним молча.
export type City = components['schemas']['City'];
export type BodyTypeInfo = components['schemas']['BodyTypeInfo'];
export type CargoListing = components['schemas']['CargoListing'];
export type CargoListingInput = components['schemas']['CargoListingInput'];
export type CargoBodyInput = components['schemas']['CargoBodyInput'];
export type CargoOffer = components['schemas']['CargoOffer'];
export type CargoSlot = components['schemas']['CargoSlot'];
export type Money = components['schemas']['Money'];

/**
 * Роль членства — перечень контракта, взятый у схемы.
 *
 * Схема тела приглашения объявлена через `anyOf` и в сгенерированных типах
 * вырождается в `unknown`: тип «канал ровно один из двух» язык типов так не
 * выражает. Перечень ролей при этом объявлен схемой членства, и берётся он
 * оттуда — рукописная копия разошлась бы с контрактом молча.
 */
export type MembershipRole = CompanyMember['role'];

/** Профиль вместе с членствами — содержимое конверта операции профиля. */
export type CurrentUserData = components['schemas']['CurrentUser']['data'];

/** Машинный код ошибки. Ветвление кода клиента идёт по нему и только по нему. */
export type ErrorCode = ApiError['code'];

/** Конверт успешного ответа: один объект либо коллекция со страницей. */
export interface DataEnvelope<T> {
  data: T;
}

export interface PageEnvelope<T> {
  data: T[];
  meta: PageMeta;
}

/**
 * Содержимое конверта data.
 *
 * Обёртка стоит одну строку здесь и экономит согласованное изменение четырёх
 * треков в тот день, когда ответу понадобятся метаданные.
 */
export function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

/** Тело ответа — конверт ошибки контракта, а не что-то похожее на него. */
export function isErrorEnvelope(body: unknown): body is ErrorEnvelope {
  if (typeof body !== 'object' || body === null || !('error' in body)) {
    return false;
  }

  const error: unknown = (body as { error: unknown }).error;

  return (
    typeof error === 'object'
    && error !== null
    && typeof (error as { code?: unknown }).code === 'string'
    && typeof (error as { message?: unknown }).message === 'string'
    && typeof (error as { request_id?: unknown }).request_id === 'string'
  );
}

/**
 * Код ошибки, если ответ — конверт ошибки.
 *
 * Разбор message или ветвление по одному HTTP-статусу — скрытый перенос
 * бизнес-правила в клиент, поэтому наружу отдаётся именно code.
 */
export function errorCode(body: unknown): ErrorCode | null {
  return isErrorEnvelope(body) ? body.error.code : null;
}

/**
 * Сообщения сервера по полям формы, если они пришли.
 *
 * Отдаётся форма, а не решение: клиент кладёт `message` рядом с элементом, чьё
 * имя равно `field`, и ни одного имени поля при этом не знает. Ветвление по
 * `field` или по коду поля было бы тем же правилом, переехавшим в клиент.
 */
export function errorDetails(body: unknown): readonly ErrorDetail[] {
  return isErrorEnvelope(body) ? (body.error.details ?? []) : [];
}

/**
 * Текст отказа, написанный сервером, — **для показа, а не для решения**.
 *
 * Решает по-прежнему машинный код: разбор текста был бы переносом правила в
 * клиент, и сторож трека красит на всяком сравнении с `message`. Отдаётся
 * текст затем, что осмысленный отказ обязан дойти до человека словами сервера:
 * «единственного директора нельзя лишить этого права» знает сервер, и сочинить
 * это на клиенте значило бы завести правило (долг `TD-67`).
 *
 * Отказ без конверта контракта текста не имеет вовсе, и `null` здесь — не
 * пустая строка, а «сервер не сказал ничего».
 */
export function errorMessage(body: unknown): string | null {
  return isErrorEnvelope(body) ? body.error.message : null;
}

/** Канал связи, который у пользователя заполнен. */
export function contactChannel(user: User): string | null {
  return user.phone ?? user.email;
}

/** Отметка регистрации пользователя. */
export function registeredAt(user: User): string {
  return user.created_at;
}

/**
 * Участия в конкретной компании — все, какие пришли.
 *
 * Отбора «действующее участие» здесь нет намеренно (AGENTS.md §2 правило 4).
 * Из чего складывается действующее участие — правило CR-002, и решает его
 * сервер: копия условия в клиенте разойдётся с ним молча, а на экране это
 * выглядит как работающий интерфейс с чужими правами.
 *
 * Порядок ленты по той же причине не вычисляется: страницы приходят курсором,
 * и сортировка страницы на клиенте спорит с порядком, которым сервер этот
 * курсор нарезал.
 */
export function membershipsIn(
  memberships: readonly Membership[],
  companyId: Company['id'],
): Membership[] {
  return memberships.filter((membership) => membership.company_id === companyId);
}

/**
 * Курсор следующей страницы либо null.
 *
 * Курсор отдаётся только вместе с has_more: непустой next_cursor на последней
 * странице даёт бесконечное дозапрашивание пустых страниц.
 */
export function nextPageCursor(meta: PageMeta): string | null {
  return meta.has_more ? meta.next_cursor : null;
}
